import { CheckCircle2, Circle, MinusCircle, XCircle } from 'lucide-react';
import type { Signal, SignalTimeline as SignalTimelineData } from '@/api/types';
import { cn } from '@/lib/utils';
import { fmtTime } from '@/lib/format';

interface Step {
  key: string;
  title: string;
  ok: boolean | null;
  detail?: React.ReactNode;
}

/**
 * 5 步链时间线 — §9.8 跟单审计:
 * 1. 信号接收(雇员单)
 * 2. 引擎决策(mode + ratio → main side + volume)
 * 3. 风控检查(通过 / 拦截原因)
 * 4. 主账户下单(realOrderId + 发送时间)
 * 5. cTrader 确认 / 成交确认(filledAt 毫秒精度)
 *
 * 真实时间戳来自后端 signals.{risk_check_passed_at, sent_to_ctrader_at,
 * ctrader_ack_at, filled_at};若字段缺省则按 status 推断是否到达。
 */
export function SignalTimeline({ signal }: { signal: Signal }) {
  const steps = computeSteps(signal);

  return (
    <ol className="relative ml-2 border-l border-line space-y-3 py-2">
      {steps.map((s) => (
        <li key={s.key} className="ml-4 relative">
          <span
            className={cn(
              'absolute -left-[22px] top-0.5 flex h-4 w-4 items-center justify-center rounded-full',
              s.ok === true && 'text-up',
              s.ok === false && 'text-down',
              s.ok === null && 'text-ink-3',
            )}
          >
            {s.ok === true ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : s.ok === false ? (
              <XCircle className="h-4 w-4" />
            ) : s.key === 'idle' ? (
              <MinusCircle className="h-4 w-4" />
            ) : (
              <Circle className="h-4 w-4" />
            )}
          </span>
          <div className="text-sm font-medium text-ink-0">{s.title}</div>
          {s.detail && <div className="mt-0.5 text-xs text-ink-2 num">{s.detail}</div>}
        </li>
      ))}
    </ol>
  );
}

function fmtTimeMs(ts: string | null | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return `${fmtTime(d)}.${String(d.getMilliseconds()).padStart(3, '0')}`;
}

function computeSteps(s: Signal): Step[] {
  const lots = (n: number) => (n / 100).toFixed(2);
  const tl: SignalTimelineData | undefined = s.timeline;

  // 优先用真实时间戳判断"是否到达";缺省时再回退到 status 推断。
  const receivedAt = tl?.receivedAt ?? s.createdAt;
  const reachedRisk = tl?.riskCheckPassedAt != null;
  const reachedSubmit = tl?.sentToCtraderAt != null;
  const reachedAck = tl?.ctraderAckAt != null;
  const reachedFill = tl?.filledAt != null;

  const blockedAtRisk =
    s.status === 'skipped_risk' || s.status === 'skipped_session' || s.status === 'skipped_user_status';
  const failedAtSubmit = s.status === 'failed';

  return [
    {
      key: 'receive',
      title: '① 信号接收',
      ok: true,
      detail: (
        <>
          {fmtTimeMs(receivedAt)} · 用户ID {s.userId} · {s.userSide} {lots(s.userVolume)} lot · 单号{' '}
          {s.userOrderId ?? '—'}
        </>
      ),
    },
    {
      key: 'decide',
      title: '② 引擎决策',
      ok: true,
      detail: (
        <>
          mode={s.followMode} · ratio={s.followRatioUsed?.toFixed(2) ?? '—'}
          {s.realSide ? (
            <>
              {' '}
              → 主账户 {s.realSide} {s.realVolume ? lots(s.realVolume) : '—'} lot
            </>
          ) : (
            <> · 计算结果:跳过</>
          )}
        </>
      ),
    },
    {
      key: 'risk',
      title: '③ 风控检查',
      ok: blockedAtRisk ? false : reachedRisk || s.status === 'executed' || s.status === 'failed' ? true : null,
      detail: blockedAtRisk ? (
        <span className="text-down-dim">拦截:{s.skipReason ?? '—'}</span>
      ) : reachedRisk ? (
        <span className="text-up">通过 · {fmtTimeMs(tl!.riskCheckPassedAt)}</span>
      ) : s.status === 'executed' || s.status === 'failed' ? (
        <span className="text-up">通过</span>
      ) : (
        <span className="text-ink-3">未到达</span>
      ),
    },
    {
      key: 'submit',
      title: '④ 主账户下单',
      ok: failedAtSubmit ? false : reachedSubmit ? true : s.status === 'executed' ? true : null,
      detail: failedAtSubmit ? (
        <span className="text-down-dim">失败:{s.skipReason ?? '上游错误'}</span>
      ) : reachedSubmit ? (
        <>
          {fmtTimeMs(tl!.sentToCtraderAt)} · cTrader order_id = {s.realOrderId ?? '—'}
          {reachedAck && (
            <span className="text-ink-3 ml-2">(ACK {fmtTimeMs(tl!.ctraderAckAt)})</span>
          )}
        </>
      ) : s.status === 'executed' ? (
        <>cTrader order_id = {s.realOrderId ?? '—'}</>
      ) : (
        <span className="text-ink-3">未触发(被前序步骤拦截)</span>
      ),
    },
    {
      key: 'fill',
      title: '⑤ 成交确认',
      ok: reachedFill ? true : s.status === 'executed' ? true : null,
      detail: reachedFill ? (
        <span className="text-up">已成交 · {fmtTimeMs(tl!.filledAt)}</span>
      ) : s.status === 'executed' ? (
        <span className="text-up">已成交(等待 ExecutionEvent 写入时间戳)</span>
      ) : (
        <span className="text-ink-3">—</span>
      ),
    },
  ];
}
